import { isq } from "../isq/index.js";
import { GoiTypeId, } from "../lab/d3/legacy/modelling/types.js";
import { UTM } from "../map/projection.js";
import { Hemisphere, LatLng, } from "../map/types.js";
import { okhsl } from "../d3-mapp/color/index.js";
import { getPolygonAverageVertex } from "../geometry/polygon.js";
export class ModellingGrid {
    param;
    _projMap;
    _toMetres;
    //to comply with the GoiDefinition interface
    id = "00000000-0000-0000-0000-000000000000";
    version = Date.now();
    name = "Whole modelled domain";
    isHole = false;
    color = okhsl("#808080");
    autoColor = okhsl("#808080");
    types = [GoiTypeId.ImpactMonitor];
    active = true;
    deleted = false;
    createdBy = "00000000-0000-0000-0000-000000000000";
    createdAt = new Date(this.version).toISOString();
    properties = {
        [GoiTypeId.ImpactMonitor]: {
            sensitivity: 1.0,
            threshold: 0.0,
        },
    };
    meta = [];
    geometry;
    constructor(param) {
        this.param = param;
        this._toMetres = -1.0;
        const size = this.getSize();
        const step = this.getStep();
        const width = size.x * step.x;
        const height = size.y * step.y;
        const utm = this.getUTM();
        const utmOrigin = this.getOrigin();
        const swUTM = [utmOrigin.x, utmOrigin.y];
        const neUTM = [swUTM[0] + width, swUTM[1] + height];
        const seUTM = [neUTM[0], swUTM[1]];
        const nwUTM = [swUTM[0], neUTM[1]];
        const swGeodetic = utm.toGeodetic(swUTM);
        const neGeodetic = utm.toGeodetic(neUTM);
        const seGeodetic = utm.toGeodetic(seUTM);
        const nwGeodetic = utm.toGeodetic(nwUTM);
        const coordinates = [
            swGeodetic,
            seGeodetic,
            neGeodetic,
            nwGeodetic,
            swGeodetic,
        ];
        const xy = [swUTM, seUTM, neUTM, nwUTM, swUTM];
        const utmAverage = getPolygonAverageVertex(xy.slice(0, -1));
        const geodeticAverage = utm.toGeodetic(utmAverage);
        this.geometry = {
            label: this.param.region,
            coordinates,
            xy,
            size: width * height,
            geodeticBounds: {
                southWest: {
                    lat: Math.min(seGeodetic.lat, swGeodetic.lat),
                    lng: Math.min(nwGeodetic.lng, swGeodetic.lng),
                },
                northEast: {
                    lat: Math.max(neGeodetic.lat, nwGeodetic.lat),
                    lng: Math.max(neGeodetic.lng, seGeodetic.lng),
                },
            },
            geodeticAverage,
            utmBounds: {
                southWest: {
                    y: swUTM[1],
                    x: swUTM[0],
                },
                northEast: {
                    y: neUTM[1],
                    x: neUTM[0],
                },
            },
            utmAverage
        };
    }
    getKey() {
        return this.param.key;
    }
    isWindFieldsSupported() {
        return !!this.param.supportsWindFields;
    }
    getRegion() {
        return this.param.region;
    }
    getProj() {
        return this.param.proj;
    }
    getSize() {
        return { x: this.param.nx, y: this.param.ny };
    }
    /**
     * Returns the step size in metres
     * @return the step size in metres
     */
    getStep() {
        return this.toCartesianMetres({ x: this.param.dx, y: this.param.dy });
    }
    getTimeStepInSeconds() {
        return this.param.timestep;
    }
    getUnit() {
        return isq.getUnit(this.param.unit);
    }
    getUTM() {
        if (this.isUTM()) {
            return UTM.get(parseInt(this.projMap()["zone"]), this.projMap()["south"] !== undefined
                ? Hemisphere.South
                : Hemisphere.North);
        }
        else {
            const center = this.getRawCenter();
            if (center) {
                return UTM.get(UTM.getZone(center.y, center.x), UTM.getHemisphere(center.y, center.x));
            }
            else {
                const origin = this.getRawOrigin();
                return UTM.get(UTM.getZone(origin.y, origin.x), UTM.getHemisphere(origin.y, origin.x));
            }
        }
    }
    /**
     * Return the origin (bottom-left)of the grid in UTM coordinates specified in metres
     *
     * @return the UTM coordinate of this grid's origin
     */
    getOrigin() {
        const origin = this.getRawOrigin();
        if (origin) {
            if (this.isUTM()) {
                return this.toCartesianMetres(origin);
            }
            else {
                // origin has been specified in geodetic coordinates
                // convert to UTM in metres
                const coord = this.getUTM().toCartesian(origin.y, origin.x);
                return { x: coord[0], y: coord[1] };
            }
        }
        else {
            const center = this.getRawCenter();
            let centerInMetres;
            if (this.isUTM()) {
                centerInMetres = this.toCartesianMetres(center);
            }
            else {
                const coord = this.getUTM().toCartesian(center.y, center.x);
                centerInMetres = { x: coord[0], y: coord[1] };
            }
            const size = this.getSize();
            const step = this.getStep();
            return {
                x: centerInMetres.x - (size.x / 2) * step.x,
                y: centerInMetres.y - (size.y / 2) * step.y,
            };
        }
    }
    /**
     * Return the center of the grid in UTM coordinates specified in the provided in metres
     *
     * @return the UTM coordinate of this grid's center
     */
    getCenter() {
        const center = this.getRawCenter();
        if (center) {
            if (this.isUTM()) {
                return this.toCartesianMetres(center);
            }
            else {
                // center has been specified in geodetic coordinates
                // convert to UTM in metres
                const coord = this.getUTM().toCartesian(center.y, center.x);
                return { x: coord[0], y: coord[1] };
            }
        }
        else {
            const origin = this.getRawOrigin();
            let originInMetres;
            if (this.isUTM()) {
                originInMetres = this.toCartesianMetres(origin);
            }
            else {
                const coord = this.getUTM().toCartesian(origin.y, origin.x);
                originInMetres = { x: coord[0], y: coord[1] };
            }
            const size = this.getSize();
            const step = this.getStep();
            return {
                x: originInMetres.x + (size.x / 2) * step.x,
                y: originInMetres.y + (size.y / 2) * step.y,
            };
        }
    }
    getGeodeticCenter() {
        const center = this.getRawCenter();
        if (center && !this.isUTM()) {
            return new LatLng(center.y, center.x);
        }
        else {
            const c = this.getCenter();
            const coord = [c.x, c.y];
            return this.getUTM().toGeodetic(coord);
        }
    }
    getParameters() {
        //return deep copy
        return JSON.parse(JSON.stringify(this.param));
    }
    projMap() {
        if (this._projMap === undefined) {
            this._projMap = ModellingGrid.parseProj(this.getProj());
        }
        return this._projMap;
    }
    static parseProj(proj) {
        const projMap = {};
        const projParts = proj.split("+");
        return projParts
            .map((projPart) => projPart.trim())
            .filter((projPart) => projPart.length > 0)
            .map((projPart) => projPart.split("="))
            .reduce((map, keyValue) => {
            map[keyValue[0].trim()] =
                keyValue.length > 1 &&
                    keyValue[1] != null &&
                    keyValue[1].trim().length > 0
                    ? keyValue[1].trim()
                    : null;
            return map;
        }, {});
    }
    isUTM() {
        return this.projMap()["proj"] === "utm";
    }
    getRawCenter() {
        if (this.param.xcent !== undefined && this.param.ycent !== undefined) {
            return { x: this.param.xcent, y: this.param.ycent };
        }
        else {
            return undefined;
        }
    }
    getRawOrigin() {
        if (this.param.xorig !== undefined && this.param.yorig !== undefined) {
            return { x: this.param.xorig, y: this.param.yorig };
        }
        else {
            return undefined;
        }
    }
    toCartesianMetres(pointInUnits) {
        return {
            x: this.toMetres(pointInUnits.x),
            y: this.toMetres(pointInUnits.y),
        };
    }
    toCartesianUnits(pointInMetres) {
        return {
            x: this.toUnits(pointInMetres.x),
            y: this.toUnits(pointInMetres.y),
        };
    }
    toMetres(lengthInUnits) {
        return lengthInUnits * this.getToMetres();
    }
    toUnits(lengthInMetres) {
        return lengthInMetres / this.getToMetres();
    }
    getToMetres() {
        if (this._toMetres <= 0.0) {
            this._toMetres = 1.0;
            if ("km" === this.projMap()["units"]) {
                this._toMetres = 1000.0;
            }
        }
        return this._toMetres;
    }
}
